import { apiSlice } from "../apiSlice";

const API_BASE_DOMAIN = process.env.REACT_APP_API_BASE_DOMAIN_PRODUCTION;

export interface Organization {
  displayName: string;
  code: string;
  status: string | { value: string; label: string };
  supportsDeliveryPoint: boolean;
  deliveryAddress?: string;
  deliveryLatitude?: number;
  deliveryLongitude?: number;
  deliveryContactName?: string;
  deliveryContactPhone?: string;
  memberCreditLimit?: number;
  creditLimit?: number;
  creditTermsDays?: number;
  allowCreditOrders?: boolean;
}

export interface WalletData {
  currentOutstanding: number;
  availableCredit: number;
  creditLimit: number;
}

export interface OrganizationResponse {
  displayName: string;
  code: string;
  status: string;
  supportsDeliveryPoint: boolean;
  deliveryPoint: {
    deliveryAddress: string;
    deliveryLatitude: number;
    deliveryLongitude: number;
    deliveryContactName: string;
    deliveryContactPhone: string;
  };
  creditSettings: {
    creditTermsDays: number;
    allowCreditOrders: boolean;
    availableCredit: number;
    memberDefaultLimit: number;
  };
  walletData: WalletData;
  createdAt: string;
  updatedAt: string;
  organizationId: string;
}

export interface UpdateOrganizationRequest {
  organizationId: string;
  data: Organization;
}

export interface GetSearchRequest {
  hits: any;
  params: string;
}

export interface AssignCustomerToOrganizationRequest {
  customerUserId: string;
  organizationId: string;
}
export interface OrganizationMember {
  id: string;
  displayName: string;
  customerId: string;
  userId: string;
  allowCreditOrders: boolean;
  addedAt: {
    _seconds: number;
    _nanoseconds: number;
  };
  email: string;
  addedBy: string;
  role: string;
}

export interface RemoveCustomerFromOrganizationRequest {
  memberId: string;
  organizationId: string;
}

const ALGOLIA_APP_ID = process.env.REACT_APP_ALGOLIA_APP_ID;
const ALGOLIA_API_KEY = process.env.REACT_APP_ALGOLIA_API_KEY;

export const orgService = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    createOrganization: builder.mutation<any, Organization>({
      query: (data) => ({
        url: `https://adminhttps-createorganization-${API_BASE_DOMAIN}-uc.a.run.app/`,
        method: "POST",
        body: data,
      }),
    }),

    getAllOrganizations: builder.query<OrganizationResponse[], void>({
      query: () => ({
        url: `https://adminhttps-getallorganizations-${API_BASE_DOMAIN}-uc.a.run.app/`,
        method: "GET",
      }),
    }),

    getOrganizationById: builder.query<OrganizationResponse, string>({
      query: (organizationId) => ({
        url: `https://adminhttps-getorganizationbyorganizationid-${API_BASE_DOMAIN}-uc.a.run.app/`,
        method: "GET",
        params: { organizationId },
      }),
    }),

    updateOrganization: builder.mutation<any, UpdateOrganizationRequest>({
      query: ({ organizationId, data }) => ({
        url: `https://adminhttps-updateorganization-${API_BASE_DOMAIN}-uc.a.run.app/`,
        method: "PUT",
        params: { organizationId },
        body: data,
      }),
    }),

    deleteOrganization: builder.mutation<void, string>({
      query: (organizationId) => ({
        url: `https://adminhttps-deleteorganization-${API_BASE_DOMAIN}-uc.a.run.app/`,
        method: "DELETE",
        params: { organizationId },
      }),
    }),

    assignCustomerToOrganization: builder.mutation<
      any,
      AssignCustomerToOrganizationRequest
    >({
      query: (data) => ({
        url: `https://adminhttps-assigncustomerstoorganization-${API_BASE_DOMAIN}-uc.a.run.app/`,
        method: "POST",
        body: data,
      }),
    }),

    removeCustomerFromOrganization: builder.mutation<
      any,
      RemoveCustomerFromOrganizationRequest
    >({
      query: (data) => ({
        url: `https://adminhttps-removecustomersfromorganization-${API_BASE_DOMAIN}-uc.a.run.app/`,
        method: "POST",
        body: data,
      }),
    }),

    getMembersByOrganizationId: builder.query<OrganizationMember[], string>({
      query: (organizationId) => ({
        url: `https://adminhttps-getmembersbyorganizationid-${API_BASE_DOMAIN}-uc.a.run.app/`,
        method: "GET",
        params: { organizationId },
      }),
    }),

    getUserSearchResults: builder.mutation<GetSearchRequest, any>({
      query: (data) => ({
        url: `https://${ALGOLIA_APP_ID}-dsn.algolia.net/1/indexes/UserSearchDocuments/query`,
        method: "POST",
        body: data,
        headers: {
          "X-Algolia-Application-Id": `${ALGOLIA_APP_ID}`,
          "X-Algolia-API-Key": `${ALGOLIA_API_KEY}`,
        },
      }),
    }),
  }),
});

export const {
  useCreateOrganizationMutation,
  useLazyGetAllOrganizationsQuery,
  useGetOrganizationByIdQuery,
  useUpdateOrganizationMutation,
  useDeleteOrganizationMutation,
  useGetUserSearchResultsMutation,
  useAssignCustomerToOrganizationMutation,
  useLazyGetMembersByOrganizationIdQuery,
  useRemoveCustomerFromOrganizationMutation,
} = orgService;
